import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Toast, ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HelpersService } from '../../../services/helpers.service';
import { ResourceService } from '../../../services/resource.service';
import { SearchService } from '../../../services/search.service';
import { DefaultService } from '../../../utils/api';
import { formatTsPipe, TranslatePipe } from '../../../utils/pipes';

@Component({
    selector: 'app-admin-users',
    templateUrl: './admin-users.component.html',
    styleUrls: ['./admin-users.component.scss'],
    standalone: false
})
export class AdminUsersComponent implements OnInit {
    items: any[] = null;

    ready: boolean = false;

    RPP: number = 20;
    startRow: number = 0;
    totalRows: number = null;
    tableLoading: boolean = true;
    SORT: any = {};
    SEARCH: any = {};

    previousParams: any = null;

    TranslatePipe = inject(TranslatePipe);

    tableHeads: any = [
        { width: '19%', name: this.TranslatePipe.transform('adminusers_th_name'), code: 'name', sortable: false },
        { width: '19%', name: this.TranslatePipe.transform('adminusers_th_email'), code: 'email', sortable: false },
        {
            width: '16%',
            name: this.TranslatePipe.transform('adminusers_th_language'),
            code: 'language',
            sortable: false
        },
        {
            width: '22%',
            name: this.TranslatePipe.transform('adminusers_th_companies'),
            code: 'companies',
            sortable: false
        },
        {
            width: '24%',
            name: this.TranslatePipe.transform('adminusers_th_lastLoginTS'),
            code: 'lastLoginTS',
            sortable: true
        },
        { width: '', name: '', code: 'actions' }
    ];

    constructor(
        public formatTsPipe: formatTsPipe,
        private DefaultService: DefaultService,
        private Router: Router,
        public HelpersService: HelpersService,
        private SearchService: SearchService,
        private Route: ActivatedRoute,
        private toastr: ToastrService,
        private ResourceService: ResourceService
    ) {
        this.Route.queryParams.subscribe((params: any) => {
            const parameters = Object.assign({}, params);
            if (parameters.id) delete parameters['id'];
            if (JSON.stringify(parameters) === JSON.stringify(this.previousParams)) {
                return;
            }
            this.startRow = (parameters.page || 1) * this.RPP - this.RPP;
            this.SEARCH = parameters.search
                ? this.getInitialSearch(JSON.parse(parameters.search))
                : this.getInitialSearch();
            this.SORT = parameters.sort ? JSON.parse(parameters.sort) : this.getInitialSort();
            let code = undefined;
            if (parameters.page !== this.previousParams?.page || this.previousParams?.page !== undefined)
                code = 'changeStartRow';
            this.getItems(code);
            this.previousParams = parameters;
        });
    }

    ngOnInit(): void {}

    getInitialSearch(SEARCH?: any) {
        if (SEARCH) {
            return {
                ...this.getDefaultSearch(),
                ...SEARCH
            };
        } else return this.getDefaultSearch();
    }

    getDefaultSearch() {
        return {};
    }

    getInitialSort() {
        return {
            code: 'lastLoginTS',
            dir: 'desc'
        };
    }

    clickSort(code: string, dir?: string) {
        this.Router.navigate([], {
            queryParams: {
                sort: JSON.stringify(
                    this.SearchService.getCleanSort(
                        this.SearchService.getSort(code, this.SORT, dir),
                        this.getInitialSort()
                    )
                )
            },
            queryParamsHandling: 'merge'
        });
    }

    getItems(code?: string) {
        // code: changestartrow
        const observables$: Observable<any>[] = [of(null)];

        const SORT = { ...this.SORT };
        const SEARCH = this.SearchService.getSearchBody(this.SEARCH, SORT, this.RPP, this.startRow);
        this.tableLoading = true;
        const items$ = this.DefaultService.usersGetAll(SEARCH.startRow, SEARCH.RPP, SEARCH.orderBy, SEARCH.term).pipe(
            tap((next: any) => {
                this.items = this.getFormattedItems(next.data);
                this.totalRows = next.rows;
                this.tableLoading = false;
                if (code === 'changeStartRow') {
                    document.getElementById('items').scrollIntoView();
                }
            })
        );
        observables$.push(items$);

        forkJoin(observables$).subscribe(() => {
            this.ready = true;
        });
    }

    search = (reset?: boolean, type?: string) => {
        if (reset) {
            this.Router.navigate([], {
                queryParams: {
                    search: null,
                    page: null
                },
                queryParamsHandling: 'merge'
            });
        } else {
            this.Router.navigate([], {
                queryParams: {
                    search: JSON.stringify(this.SearchService.getCleanSearch(this.SEARCH)),
                    page: null
                },
                queryParamsHandling: 'merge'
            });
        }
    };

    getFormattedItems(items) {
        return items.map((item) => {
            return this.getFormattedItem(item);
        });
    }

    getFormattedItem(item) {
        if (item.lastLoginTS) {
            item.lastLoginTS = `${this.formatTsPipe.transform(
                item.lastLoginTS,
                'D MMM YYYY'
            )} ${this.TranslatePipe.transform('misc_date_at')} ${this.formatTsPipe.transform(
                item.lastLoginTS,
                'HH:mm'
            )}`;
        } else item.lastLoginTS = '-';
        const actions = [];
        if (item.firstlogin) {
            actions.push({
                name: this.TranslatePipe.transform('adminusers_send_firstlogin'),
                code: 'firstlogin',
                icon: 'reset-lock'
            });
        }
        if (!item.firstlogin) {
            actions.push({
                name: this.TranslatePipe.transform('adminusers_send_reset_password'),
                code: 'resetpassword',
                icon: 'reset-lock'
            });
        }
        item = {
            ...item,
            name: { type: 'title', title: `${item.firstname || '-'} ${item.lastname || ''}` },
            email: { type: 'default', value: item.email },
            language: { type: 'default', value: this.ResourceService.getLanguageByCode(item.language).title },
            companies: {
                type: 'arrayTooltip',
                items: item?.companies?.map((item) => {
                    return item.company;
                })
            },
            lastLoginTS: { type: 'default', value: item.lastLoginTS },
            actions: {
                type: 'actions',
                actions: actions
            }
        };
        return item;
    }

    tableClick(item: any, head: string) {
        this.Router.navigate([this.HelpersService.getUrl('admin/companies/' + item.id)]);
    }

    actionClick(item: any, action: string) {
        switch (action) {
            case 'firstlogin':
                this.DefaultService.usersSendResetPasswordOrFirstlogin(item.id).subscribe((next) => {
                    this.toastr.success(
                        this.TranslatePipe.transform('toast_user_sent_firstlogin_msg'),
                        this.TranslatePipe.transform('toast_user_sent_firstlogin_title')
                    );
                });
                break;
            case 'resetpassword':
                this.DefaultService.usersSendResetPasswordOrFirstlogin(item.id).subscribe((next) => {
                    this.toastr.success(
                        this.TranslatePipe.transform('toast_user_sent_resetpassword_msg'),
                        this.TranslatePipe.transform('toast_user_sent_resetpassword_title')
                    );
                });
                break;
        }
    }

    resetStartRow() {
        this.startRow = 0;
        this.getItems();
    }

    changeStartRow(startRow: number) {
        this.startRow = startRow;
        this.getItems('changeStartRow');
    }
}
